import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone, X, Gift, CreditCard, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";
import VoucherDialog from "@/components/site/VoucherDialog";
import LanguageToggle from "@/components/site/LanguageToggle";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";

export default function Header() {
  const { t } = useT();
  const { user, isAuthed } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "/#services", label: t("nav.services") },
    { href: "/#how-it-works", label: t("nav.howItWorks") },
    { href: "/#schedule", label: t("nav.schedule") },
    { href: "/#reviews", label: t("nav.reviews") },
    { href: "/blog", label: t("nav.blog") },
    { href: "/#faq", label: t("nav.faq") },
    { href: "/#location", label: t("nav.location") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/oxford_logo.jpg`}
            alt="Oxford Cleaners — Veteran and Family Owned"
            className="h-11 w-11 lg:h-12 lg:w-12 rounded-2xl object-cover shadow-[0_8px_24px_-8px_rgba(2,132,199,0.45)] ring-1 ring-slate-200" loading="lazy" decoding="async" />
          <span className="font-display text-xl font-bold tracking-tight text-brand-navy">
            Oxford <span className="text-brand-blue">Cleaners</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            data-testid="header-phone-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-navy hover:text-brand-blue transition-colors"
          >
            <Phone className="h-4 w-4" />
            {BUSINESS.phone}
          </a>
          <a
            href={BUSINESS.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-pay-online"
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3.5 h-10 text-sm font-semibold transition-colors"
          >
            <CreditCard className="h-4 w-4" /> {t("cta.payOnline")}
          </a>
          <VoucherDialog
            testIdPrefix="header-voucher"
            trigger={
              <Button
                data-testid="header-voucher-btn"
                className="rounded-full bg-amber-500 hover:bg-amber-600 text-white h-10 px-4 font-semibold gap-1.5 shadow-[0_8px_18px_-8px_rgba(245,158,11,0.7)]"
              >
                <Gift className="h-4 w-4" /> {t("cta.tenOff")}
              </Button>
            }
          />
          {isAuthed ? (
            <Button
              asChild
              variant="outline"
              data-testid="header-profile-link"
              className="rounded-full h-10 px-4 font-semibold gap-1.5"
            >
              <Link to="/profile">
                <UserIcon className="h-4 w-4" /> {user?.name?.split(" ")[0] || "Account"}
              </Link>
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              data-testid="header-signin-link"
              className="rounded-full h-10 px-4 font-semibold"
            >
              <Link to="/login">Sign in</Link>
            </Button>
          )}
          <Button
            asChild
            data-testid="header-schedule-cta"
            className="rounded-full bg-brand-blue hover:bg-brand-navy text-white px-5 h-10 font-semibold"
          >
            <a href="/#schedule">{t("cta.schedulePickup")}</a>
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageToggle />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-lg hover:bg-slate-100"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.slice(1)}`}
                className="py-2 text-base font-medium text-slate-700"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${BUSINESS.phoneTel}`}
              className="inline-flex items-center gap-2 py-2 text-base font-semibold text-brand-navy"
            >
              <Phone className="h-4 w-4" /> {BUSINESS.phone}
            </a>
            <a
              href={BUSINESS.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-pay-online"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 h-11 text-base font-semibold transition-colors"
            >
              <CreditCard className="h-4 w-4" /> {t("cta.payOnline")}
            </a>
            <VoucherDialog
              testIdPrefix="mobile-voucher"
              trigger={
                <Button
                  data-testid="mobile-voucher-btn"
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white h-11 gap-2 font-semibold"
                >
                  <Gift className="h-4 w-4" /> {t("cta.claimTenVoucher")}
                </Button>
              }
            />
            {isAuthed ? (
              <Button asChild variant="outline" className="rounded-full h-11 gap-2" data-testid="mobile-profile-link">
                <Link to="/profile" onClick={() => setOpen(false)}>
                  <UserIcon className="h-4 w-4" /> My account
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" className="rounded-full h-11" data-testid="mobile-signin-link">
                <Link to="/login" onClick={() => setOpen(false)}>Sign in</Link>
              </Button>
            )}
            <Button
              asChild
              className="rounded-full bg-brand-blue hover:bg-brand-navy text-white h-11 mt-1"
            >
              <a href="/#schedule" onClick={() => setOpen(false)}>
                {t("cta.schedulePickup")}
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
